package com.siriusxm.pia.views.account

import androidx.compose.runtime.Composable
import com.siriusxm.pia.Application
import com.siriusxm.pia.UserGroup
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.IconSize
import com.siriusxm.pia.components.icon
import org.jetbrains.compose.web.dom.Div

/** Regex matching Cognito identity provider identifiers (e.g. us-east-2_abc123_Okta) */
private val COGNITO_PROVIDER_ID_PATTERN = Regex("^us-\\w+-\\d+_.+")

/**
 * Returns Cognito groups that are not mapped to a known [UserGroup] entry and are not
 * internal identity-provider identifiers. These represent custom or org-specific groups
 * the user belongs to.
 */
internal fun extraCognitoGroups(allGroups: Collection<String>): List<String> {
    val knownGroupIds = UserGroup.entries.map { it.id }.toSet()
    return allGroups.filter { it !in knownGroupIds && !it.matches(COGNITO_PROVIDER_ID_PATTERN) }
}

/**
 * Renders the user's group/role badges. Uses green for admin-level groups
 * ([UserGroup.AggregatorAdmin]) and blue for all others, matching the admin badge
 * color used in producer/consumer membership tables. Falls back to a gray "Basic"
 * badge when the user has no active groups. Also renders gray badges for any
 * non-standard Cognito groups not mapped to a [UserGroup] entry.
 *
 * @param showIcons Whether to display material icons next to each group badge
 * @param showExtraGroups Whether to show non-standard Cognito group badges
 */
@Composable
internal fun UserGroupBadges(showIcons: Boolean = false, showExtraGroups: Boolean = false) {
    val viewer = Application.viewer
    val activeGroups = UserGroup.entries.filter { viewer.isInGroup(it) }

    Div({ classes(AccountStyles.badgeRow) }) {
        if (activeGroups.isNotEmpty()) {
            activeGroups.forEach { group ->
                val color = if (group == UserGroup.AggregatorAdmin) BadgeColor.GREEN else BadgeColor.BLUE
                if (showIcons) {
                    Div({ classes(AccountStyles.pageGroupRow) }) {
                        group.iconName?.let { iconName ->
                            icon(iconName) {
                                size = IconSize.SMALL
                                title = group.title
                            }
                        }
                        Badge(group.title, color)
                    }
                } else {
                    Badge(group.title, color)
                }
            }
        } else {
            Badge("Basic", BadgeColor.GRAY)
        }

        if (showExtraGroups) {
            extraCognitoGroups(viewer.groups).forEach { groupId ->
                Badge(groupId, BadgeColor.GRAY)
            }
        }
    }
}
